import type {
  KnowledgeFilterOption,
  KnowledgeKind,
  KnowledgeProblemType,
  KnowledgeProductOption,
  KnowledgeSourceType,
  KnowledgeStatus,
} from '@/types/knowledge'

export const UNCLASSIFIED_PRODUCT_LABEL = '未归类'

export const KNOWLEDGE_STATUS_COLOR: Record<KnowledgeStatus, string> = {
  published: 'green',
  draft: 'blue',
  archived: 'default',
}

export const KNOWLEDGE_STATUS_LABEL: Record<KnowledgeStatus, string> = {
  published: '已发布',
  draft: '草稿',
  archived: '已归档',
}

// 与后端 catalog.py 的展示字典保持一致，前端只做颜色补充
export const KNOWLEDGE_KIND_LABEL: Record<KnowledgeKind, string> = {
  capability: '产品通用知识',
  issue: '问题知识',
}

export const KNOWLEDGE_KIND_COLOR: Record<KnowledgeKind, string> = {
  capability: 'geekblue',
  issue: 'orange',
}

export const KNOWLEDGE_PROBLEM_TYPE_LABEL: Record<KnowledgeProblemType, string> = {
  hardware: '硬件/设备故障',
  software_bug: '软件缺陷',
  av_playback: '音视频播放',
  data_inconsistency: '数据不一致',
  network: '网络连通',
  configuration: '配置部署',
  integration: '第三方对接',
  usage: '使用指导',
  requirement: '需求优化',
  process: '流程协同',
  other: '其他',
}

export const KNOWLEDGE_PROBLEM_TYPE_COLOR: Record<KnowledgeProblemType, string> = {
  hardware: 'red',
  software_bug: 'volcano',
  av_playback: 'purple',
  data_inconsistency: 'magenta',
  network: 'cyan',
  configuration: 'blue',
  integration: 'geekblue',
  usage: 'green',
  requirement: 'gold',
  process: 'lime',
  other: 'default',
}

export const KNOWLEDGE_SOURCE_TYPE_LABEL: Record<KnowledgeSourceType, string> = {
  manual: '人工录入',
  after_sales_issue: '售后问题平台',
  zentao_task: '禅道工单',
  wechat_conversation: '企微群会话',
  brochure: '产品彩页',
  wiki: '产品 Wiki',
  document: '产品资料',
  closed_ticket: '已关闭工单',
  curated: '二次梳理（AI 归并）',
}

export const KNOWLEDGE_SOURCE_TYPE_COLOR: Record<KnowledgeSourceType, string> = {
  manual: 'blue',
  after_sales_issue: 'purple',
  zentao_task: 'orange',
  wechat_conversation: 'green',
  brochure: 'gold',
  wiki: 'cyan',
  document: 'cyan',
  closed_ticket: 'default',
  curated: 'magenta',
}

/**
 * 产品展示口径。产品主分类以**产品彩页**为准（product_key 形如 brochure:medical）。
 * product_key 为空表示没落到具体彩页产品，此时：
 * - 有 product_name（只识别到行业）→ 展示行业名并标注"仅行业"
 * - 都没有 → 统一显示"未归类"
 */
export function knowledgeProductDisplay(
  productKey: string,
  productName: string,
  productLine: string,
): string {
  if (!productKey) {
    if (productName && productName !== UNCLASSIFIED_PRODUCT_LABEL) {
      return `${productName}（仅行业）`
    }
    return productLine ? `${productLine}（仅行业）` : UNCLASSIFIED_PRODUCT_LABEL
  }
  const name = productName || UNCLASSIFIED_PRODUCT_LABEL
  return productLine && productLine !== name ? `${name}（${productLine}）` : name
}

/** 问题类型颜色。后端可能下发未知值，统一回退到 default */
export function knowledgeProblemTypeColor(value: string): string {
  return KNOWLEDGE_PROBLEM_TYPE_COLOR[value as KnowledgeProblemType] || 'default'
}

/** 展示字典 -> antd Select 选项（字典是标签的唯一事实来源） */
function toSelectOptions(map: Record<string, string>) {
  return Object.keys(map).map((value) => ({ value, label: map[value] }))
}

/** 知识形态选项（录入/编辑表单用） */
export const KNOWLEDGE_KIND_OPTIONS = toSelectOptions(KNOWLEDGE_KIND_LABEL)

/** 问题类型选项（录入/编辑表单用，与后端枚举口径一致，不依赖接口可用性） */
export const KNOWLEDGE_PROBLEM_TYPE_OPTIONS = toSelectOptions(KNOWLEDGE_PROBLEM_TYPE_LABEL)

/**
 * 产品选项兜底：表单允许「未归类」（空 product_key），必须保证它始终可选。
 *
 * 后端在存在"仅行业"知识时会下发 `product_key === ''` 但 `product_line` 非空的行
 * （例如"智慧医疗（仅行业）"）。这些行的 key 已经是空串，本身就是"未归类"这个取值，
 * 所以判定要覆盖所有空 key 的行 —— 否则 Select 里会出现多个 value='' 的重复项。
 */
export function withUnclassifiedProduct(
  options: KnowledgeProductOption[],
): KnowledgeProductOption[] {
  if (options.some((item) => !item.product_key)) return options
  return [
    {
      product_key: '',
      product_name: UNCLASSIFIED_PRODUCT_LABEL,
      product_line: '',
      count: 0,
    },
    ...options,
  ]
}

/** 筛选器选项 -> antd Select 选项（带条数，便于判断口径规模） */
export function toFilterSelectOptions(options: KnowledgeFilterOption[]) {
  return options.map((item) => ({
    value: item.value,
    label: `${item.label}（${item.count}）`,
  }))
}

/** 产品选项 -> antd Select 选项。值为彩页 product_key（空串 = 未归类）。 */
export function toProductSelectOptions(options: KnowledgeProductOption[]) {
  return options.map((item) => ({
    value: item.product_key,
    label: knowledgeProductDisplay(item.product_key, item.product_name, item.product_line),
  }))
}

/** 发布 / 归档 / 触发向量索引补建：manager 与 admin */
export function canManageKnowledge(roles: string[]): boolean {
  return roles.includes('manager') || roles.includes('admin')
}

/** 人工录入 / 编辑知识：与后端 _EDITOR_ROLES 对齐 */
export function canEditKnowledge(roles: string[]): boolean {
  return (
    canManageKnowledge(roles) ||
    roles.includes('service_agent') ||
    roles.includes('tech_owner')
  )
}
